import './styles.css'

import { useEffect, useRef } from 'react'

import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { EditorHeaderSlot } from '@editor/EditorProvider'
import LoadingPage from '@editor/components/LoadingPage'
import PageErrorHandler from '@editor/components/PageErrorHandler'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { RootState, useAppDispatch } from '@editor/store/app'
import { Canvas } from '@react-three/fiber'
import { IoWarning } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { LinearSRGBColorSpace, NoToneMapping } from 'three'

import AreaSwitcher from '../common/AreaSwitcher'
import MissingPCDFIle from '../common/MissingPCDFIle'
import usePCDLoader from '../hooks/usePCDLoader'
import ButtonPanel from './components/ButtonPanel'
import CameraToolbarManager from './components/CameraToolbarManager'
import CurrentHoveringAreaTooltip from './components/CurrentHoveringAreaTooltip'
import EventHandlers, { EventHandlersForwardRef } from './components/EventHandlers'
import FineTuneScene from './components/FineTuneScene'
import MainScene from './components/MainScene'
import Overlay from './components/Overlay'
import PCDActions, { PCDActionsForwardRef } from './components/PCDActions'
import Render from './components/Render'
import SplitScene from './components/SplitScene'
import StepsManager from './components/StepsManager'
import { SceneEventHandlers, Tools, ViewSides } from './definitions'
import { usePCDAlignment } from './hooks/usePCDAlignment'
import { setLeftProgress, setRightProgress } from './store'

const PCDAlignment = () => {
  const { potree: leftPotree } = usePCDLoader()
  const { potree: rightPotree } = usePCDLoader()
  const {
    isAllowedToModify,
    canvasRef,
    detectSwitchingSide,

    sceneLeftRef,
    sceneRightRef,
    sceneFineTuneRef,
    sceneMainRef,

    arcballControlsLeftRef,
    arcballControlsRightRef,
    arcballControlsFineTuneRef,
    arcballControlsMainRef,

    cameraLeft,
    cameraRight,
    fineTuneCamera,
    mainCamera,

    inspectionAreas,
    leftInspectionArea,
    rightInspectionArea,

    handleResize,
  } = usePCDAlignment()

  // Store
  const dispatch = useAppDispatch()
  const isPageLoading = useSelector((state: RootState) => state.page.isLoading)
  const isLoading = useSelector((state: RootState) => state.pcdAlignment.isLoading)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)

  // Refs
  const eventHandlersRef = useRef<EventHandlersForwardRef>(null)
  const mainSceneRef = useRef<SceneEventHandlers>({})
  const fineTuneSceneRef = useRef<SceneEventHandlers>({})
  const leftSceneRef = useRef<SceneEventHandlers>({})
  const rightSceneRef = useRef<SceneEventHandlers>({})
  const pcdActionRef = useRef<PCDActionsForwardRef>(null)

  /*
   * Initialize the canvas size
   */
  useEffect(() => {
    if (!isLoading) {
      handleResize()
    }
  }, [isLoading, handleResize])

  // No inspection area
  if (!isPageLoading && !inspectionArea) {
    return (
      <PageErrorHandler>
        <VStack p={0} m={0} h="100svh" gap={0}>
          <EditorHeaderSlot />
          <Flex
            bg="black"
            flex={1}
            w="100%"
            color="white"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={3}
          >
            <IoWarning size="7rem" color="yellow" />
            <Text fontSize="lg" color="gray2.300">
              エリアが見つかりませんでした。
            </Text>
          </Flex>
        </VStack>
      </PageErrorHandler>
    )
  }

  // Down-sampling is still running
  if (
    inspectionArea &&
    (inspectionArea.downsample_status?.status === FileProcessingStatus.RUNNING ||
      inspectionArea.mesh_bundle_status?.status === FileProcessingStatus.RUNNING)
  ) {
    return (
      <PageErrorHandler>
        <VStack p={0} m={0} h="100svh" gap={0}>
          <EditorHeaderSlot />
          <Flex
            bg="black"
            flex={1}
            w="100%"
            color="white"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={3}
          >
            <LoadingPage text="ファイルを変換中..." />
          </Flex>
        </VStack>
      </PageErrorHandler>
    )
  }

  // PCD file is not uploaded yet
  if (
    inspectionArea &&
    (!inspectionArea.down_sampled_file?.name ||
      inspectionArea.downsample_status?.status !== FileProcessingStatus.SUCCEEDED)
  ) {
    return <MissingPCDFIle pageName="位置合わせ" />
  }

  return (
    <PageErrorHandler>
      <VStack id="pcd-alignment">
        <EditorHeaderSlot />

        {isPageLoading ? <LoadingPage /> : null}

        {!isLoading && !isPageLoading && isAllowedToModify ? (
          <ButtonPanel leftInspectionArea={leftInspectionArea} rightInspectionArea={rightInspectionArea} />
        ) : null}

        {currentTool === Tools.PCDAlignment && <StepsManager />}

        <PCDActions ref={pcdActionRef} camera={mainCamera} />

        {currentTool === Tools.Overview && <CurrentHoveringAreaTooltip />}

        <Overlay leftInspectionArea={leftInspectionArea} />

        <CameraToolbarManager
          isAllowedToModify={isAllowedToModify}
          leftInspectionArea={leftInspectionArea}
          rightInspectionArea={rightInspectionArea}
          mainCamera={mainCamera}
          cameraLeft={cameraLeft}
          cameraRight={cameraRight}
          fineTuneCamera={fineTuneCamera}
          arcballControlsLeftRef={arcballControlsLeftRef}
          arcballControlsRightRef={arcballControlsRightRef}
          arcballControlsFineTuneRef={arcballControlsFineTuneRef}
          arcballControlsMainRef={arcballControlsMainRef}
        />

        <Flex id="canvas-container">
          {currentTool === Tools.PCDAlignment && (
            <Box id="area-selector">
              <AreaSwitcher
                containerId="left-area-switcher"
                inspectionAreas={inspectionAreas}
                currentArea={leftInspectionArea}
                onSwitched={(area) => pcdActionRef.current?.onLeftAreaSwitch(area)}
              />
            </Box>
          )}

          <Canvas
            ref={canvasRef}
            id="canvasBox"
            style={{ cursor: 'crosshair' }}
            onMouseMove={(e) => {
              detectSwitchingSide(e)

              if (!eventHandlersRef.current) return
              eventHandlersRef.current.onMove(e)
            }}
            onCreated={({ gl }) => {
              gl.setScissorTest(true)
              gl.toneMapping = NoToneMapping
              gl.outputColorSpace = LinearSRGBColorSpace
            }}
            onMouseDown={(ev) => {
              if (!eventHandlersRef.current) return
              eventHandlersRef.current.onDown(ev)
            }}
            onMouseUp={(ev) => {
              if (!eventHandlersRef.current) return
              eventHandlersRef.current.onUp(ev)
            }}
            onClick={(ev) => {
              if (!eventHandlersRef.current) return
              eventHandlersRef.current.onClick(ev)
            }}
          >
            <Render
              sceneLeftRef={sceneLeftRef}
              sceneRightRef={sceneRightRef}
              fineTuneSceneRef={sceneFineTuneRef}
              mainSceneRef={sceneMainRef}
              cameraRight={cameraRight}
              cameraLeft={cameraLeft}
              fineTuneCamera={fineTuneCamera}
              mainCamera={mainCamera}
              leftPotree={leftPotree}
              rightPotree={rightPotree}
            />

            <SplitScene
              ref={rightSceneRef}
              sceneRef={sceneRightRef}
              controlsRef={arcballControlsRightRef}
              side={ViewSides.Right}
              camera={cameraRight}
              inspectionArea={rightInspectionArea}
              onProgress={(percent) => {
                dispatch(setRightProgress(percent))
              }}
              potree={rightPotree}
            />
            <SplitScene
              ref={leftSceneRef}
              sceneRef={sceneLeftRef}
              controlsRef={arcballControlsLeftRef}
              side={ViewSides.Left}
              camera={cameraLeft}
              inspectionArea={leftInspectionArea}
              onProgress={(percent) => {
                dispatch(setLeftProgress(percent))
              }}
              potree={leftPotree}
            />

            <FineTuneScene
              ref={fineTuneSceneRef}
              sceneRef={sceneFineTuneRef}
              controlsRef={arcballControlsFineTuneRef}
              camera={fineTuneCamera}
              leftInspectionArea={leftInspectionArea}
              rightInspectionArea={rightInspectionArea}
              potree={rightPotree}
            />

            <MainScene
              ref={mainSceneRef}
              sceneRef={sceneMainRef}
              controlsRef={arcballControlsMainRef}
              camera={mainCamera}
              potree={rightPotree}
            />

            <EventHandlers
              ref={eventHandlersRef}
              leftSceneRef={leftSceneRef}
              rightSceneRef={rightSceneRef}
              fineTuneSceneRef={fineTuneSceneRef}
              mainSceneRef={mainSceneRef}
            />

            <ambientLight intensity={Math.PI} />
            <color attach="background" args={['#000000']} />
          </Canvas>
        </Flex>
      </VStack>
    </PageErrorHandler>
  )
}

export default PCDAlignment
